import { Calendar as CalendarIcon } from 'lucide-react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * CalendarPicker — the 0 / 1 / many calendar picker for the Schedule tab
 * (issue #503).
 *
 * Reuses the three-state picker CONTRACT from components/inbox/ReplyAsSelector.jsx
 * (0 options → nothing / 1 option → fixed bordered box / many → Radix Select),
 * but the option shape is deliberately different: calendar identity
 * `{value, label, provider, sendable}` (built by hooks/useScheduleCapabilities),
 * NOT ReplyAsSelector's mailbox identity `{address, kind?, value?, sendable?}`
 * which renders `opt.address`.
 *
 * @param {Array<{value:string, label:string, provider:string, sendable:boolean}>} options
 * @param {string} value   - selected calendar id ('google' | 'outlook')
 * @param {(value: string) => void} onChange
 * @param {'stacked'|'inline'} [layout] - `inline` puts label and control on one
 *   row with no helper line, for the calendar editor's action bar (#680).
 * @param {boolean} [disabled] - an existing event cannot change its host.
 *
 * Labelled "Hosting calendar" (#680 / #685): the rail's "My calendars" means
 * tags, so the provider control stopped saying "Calendar". The booking-link
 * editor (#729) uses the same words.
 */
export default function CalendarPicker({ options, value, onChange, layout = 'stacked', disabled = false }) {
    // 0 options → render nothing: the toolbar button is gated upstream, so an
    // empty list only means the status hooks are still resolving.
    if (!options || options.length === 0) return null;
    const inline = layout === 'inline';
    const label = <Label className={inline ? 'text-[12.5px] font-medium text-[#57504B] whitespace-nowrap' : 'text-xs'}>Hosting calendar</Label>;
    const wrap = inline ? 'flex items-center gap-2' : 'space-y-1';
    // One option, or an existing event whose host cannot change: a fixed box.
    const fixed = options.length === 1 || disabled
        ? (options.find((o) => o.value === value) || options[0])
        : null;

    if (fixed) {
        return (
            <div className={wrap}>
                {label}
                <div
                    className={inline
                        ? 'flex items-center gap-2 h-8 px-3 rounded-[9px] border border-[#E4E1DB] bg-[#FBFAF9]'
                        : 'flex items-center gap-2 px-3 py-2 rounded-md border border-stone-200 bg-white'}
                    data-testid="calendar-picker-fixed"
                    title={disabled ? 'An existing event stays on the calendar that hosts it' : undefined}
                >
                    <CalendarIcon className="w-3.5 h-3.5 text-stone-400" />
                    <span className={inline ? 'text-[12.5px] text-[#3A3532] font-medium' : 'text-sm text-stone-700 font-medium'}>{fixed.label}</span>
                </div>
                {!inline && (
                    <p className="text-[10px] text-stone-400">
                        Invitation is sent by {fixed.label} from your own address.
                    </p>
                )}
            </div>
        );
    }

    return (
        <div className={wrap}>
            {label}
            {/* `?? undefined`: Radix's Select treats null as neither a controlled
                value nor "uncontrolled" (ReplyAsSelector precedent). */}
            <Select value={value ?? undefined} onValueChange={onChange}>
                <SelectTrigger data-testid="calendar-picker-select" className={inline ? 'bg-white h-8 rounded-[9px] border-[#E4E1DB] text-[12.5px] min-w-[170px]' : 'bg-white'}>
                    <SelectValue placeholder="Select calendar" />
                </SelectTrigger>
                <SelectContent>
                    {options.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            {!inline && (
                <p className="text-[10px] text-stone-400">
                    The event is created in the selected calendar only.
                </p>
            )}
        </div>
    );
}
